import React, { useEffect } from 'react'
import { connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import { Map, List, fromJS } from 'immutable'
import PUBNUB from 'pubnub'
import { v4 as uuidv4 } from 'uuid'
import _map from 'lodash/map'
import { getBoundActions } from '../../actions/index'
import { connect as connectStaticText } from '../StaticText/connect'
import { blockedChatUsersKey, chatListStoreKey } from '../../services/live-access-events/reducers'
import { selectCurrentUserProfilePicture } from '../../services/community/identity/profile/redux/selectors'
import { getLogger } from '../../log/index'
import { H3 } from '../Heading/index'
import ChatHistory from '../EventsPage/Chat/ChatHistory'
import ChatHeader from '../EventsPage/Chat/ChatHeader'
import ChatInput from '../EventsPage/Chat/ChatInput'
import { getChatInfo } from './LiveExperience'

const EMPTY_AVATAR_URL = 'https://brooklyn.gaia.com/v1/image-render/3c0095e7-1eff-467f-942a-61039e9cf6d0/test'

const ANON_USERNAME_PREFIX = 'zzz'

// Simplified Chat component for pop-out - no ChatCTA, no entitlement checks
class PopOutChat extends React.Component {
  constructor (props) {
    super(props)
    const {
      pubNubMainChannel,
      pubNubTechChannel,
    } = props

    this.PubNub = null
    this.lastMessageTimestamp = {}

    this.listener = {
      message: this.handleMessageReceived,
      signal: this.handleSignalReceived,
    }

    // Initialize state with empty lists for channels
    const initialState = {
      currentChat: pubNubMainChannel || '',
    }
    if (pubNubMainChannel) {
      initialState[pubNubMainChannel] = List()
    }
    if (pubNubTechChannel) {
      initialState[pubNubTechChannel] = List()
    }
    this.state = initialState
  }

  componentDidMount = () => {
    const { props } = this
    const { getBlockedUsers, pubNubMainChannel } = props
    if (getBlockedUsers && pubNubMainChannel) {
      getBlockedUsers(pubNubMainChannel)
    }
    // Only initialize PubNub if we have all required props
    if (props.pubNubPublishKey && props.pubNubSubscribeKey
      && props.pubNubMainChannel && props.pubNubTechChannel) {
      this.initPubNub()
    }
  }

  componentDidUpdate (prevProps) {
    const { props } = this
    // Re-initialize PubNub if channels or keys change
    const channelsChanged = prevProps.pubNubMainChannel !== props.pubNubMainChannel
      || prevProps.pubNubTechChannel !== props.pubNubTechChannel
    const keysChanged = prevProps.pubNubPublishKey !== props.pubNubPublishKey
      || prevProps.pubNubSubscribeKey !== props.pubNubSubscribeKey

    if (channelsChanged || keysChanged) {
      this.closePubNub()
      if (props.pubNubPublishKey && props.pubNubSubscribeKey
        && props.pubNubMainChannel && props.pubNubTechChannel) {
        this.initPubNub()
      }
    }
  }

  componentWillUnmount () {
    this.closePubNub()
  }

  initPubNub () {
    const { props } = this
    const {
      pubNubSubscribeKey,
      pubNubTechChannel,
      pubNubMainChannel,
      pubNubPublishKey,
      username,
    } = props

    // Validate required props
    if (!pubNubPublishKey || !pubNubSubscribeKey || !pubNubMainChannel || !pubNubTechChannel) {
      getLogger().error('ChatPopOut: Missing required PubNub configuration')
      return
    }

    // Close existing connection if any
    if (this.PubNub) {
      this.closePubNub()
    }

    this.PubNub = new PUBNUB({
      publish_key: pubNubPublishKey,
      subscribe_key: pubNubSubscribeKey,
      ssl: true,
    })

    this.PubNub.addListener(this.listener)
    this.username = username || `${ANON_USERNAME_PREFIX}-${uuidv4()}`

    if (!global.BROWSER_TEST) {
      this.PubNub.subscribe({
        channels: [
          pubNubMainChannel,
          pubNubTechChannel,
        ],
      })
      // Fetch history after a short delay to ensure subscription is established
      setTimeout(() => {
        this.fetchHistory(pubNubMainChannel)
        this.fetchHistory(pubNubTechChannel)
      }, 100)
    }
  }

  closePubNub () {
    const { props } = this
    const { pubNubMainChannel, pubNubTechChannel } = props
    if (this.PubNub) {
      this.PubNub.removeListener(this.listener)
      this.PubNub.unsubscribe({
        channels: [
          pubNubMainChannel,
          pubNubTechChannel,
        ],
      })
      this.PubNub = null
    }
  }

  fetchHistory (channel) {
    if (!this.PubNub) {
      getLogger().error(`ChatPopOut: Cannot fetch history - PubNub not initialized for channel: ${channel}`)
      return
    }
    if (!channel) {
      getLogger().error('ChatPopOut: Cannot fetch history - channel is empty')
      return
    }

    this.PubNub.history(
      {
        start: this.lastMessageTimestamp[channel],
        stringifiedTimeToken: true,
        count: 15,
        channel,
      },
      (status, response) => {
        if (status.error) {
          getLogger().error(`
          ChatPopOut: Error fetching history for username: ${this.username}
          channel: ${channel}
          with subscribe key: ${this.props.pubNubSubscribeKey}
          error: ${status.errorData ? JSON.stringify(status.errorData) : status.error}
        `)
          return
        }

        const {
          startTimeToken,
          messages,
        } = response

        // Always update timestamp even if no messages
        if (startTimeToken) {
          this.lastMessageTimestamp[channel] = startTimeToken
        }

        if (messages && messages.length > 0) {
          const { state } = this
          const existing = state[channel] || List()
          const entries = _map(messages, (m) => m.entry)
          const messageList = fromJS(entries)

          this.setState(() => ({
            [channel]: messageList.concat(existing),
          }))
        }
      },
    )
  }

  toggleChannel = (e) => {
    e.preventDefault()
    const { props } = this
    const { pubNubMainChannel, pubNubTechChannel } = props
    this.setState((state) => ({
      currentChat: state.currentChat === pubNubMainChannel
        ? pubNubTechChannel
        : pubNubMainChannel,
    }))
  }

  handleSignalReceived = (response) => {
    const { props } = this
    const { channel, message } = response
    const {
      pubNubMainChannel, pubNubTechChannel, getBlockedUsers,
    } = props
    if (channel === pubNubMainChannel && message === 'refresh') {
      this.resetHistory()
      this.fetchHistory(pubNubMainChannel)
      this.fetchHistory(pubNubTechChannel)
      if (getBlockedUsers) {
        getBlockedUsers(pubNubMainChannel)
      }
    }
  }

  resetHistory () {
    const { props } = this
    const { pubNubMainChannel, pubNubTechChannel } = props
    this.lastMessageTimestamp = {}
    this.setState(() => ({
      [pubNubMainChannel]: List(),
      [pubNubTechChannel]: List(),
    }))
  }

  handleMessageReceived = (response) => {
    this.setState((state) => {
      const { channel, message } = response
      const prev = state[channel] || List()
      const next = prev.push(fromJS(message))
      return { [channel]: next }
    })
  }

  sendMessage = (message) => {
    const messageObj = {
      ImageUrl: this.props.imageUrl || EMPTY_AVATAR_URL,
      When: new Date().valueOf(),
      Who: this.username,
      What: message,
    }
    if (this.PubNub) {
      this.PubNub.publish({
        channel: this.state.currentChat,
        message: messageObj,
      }, (status) => {
        if (status.error) {
          getLogger().error(`
          Chat:  Error publishing message for username: ${this.username}
          channel: ${this.state.currentChat} with publish
          key: ${this.props.pubNubPublishKey}
        `)
        }
      })
    }
  }

  onFetchHistory = () => {
    const { state } = this
    const { currentChat } = state
    this.fetchHistory(currentChat)
  }

  render () {
    const { state, props } = this
    const {
      pubNubTechChannel,
      pubNubMainChannel,
      blockedUsers,
      staticText,
      username,
      imageUrl,
    } = props

    const mainBlockedUsers = blockedUsers && blockedUsers.getIn([pubNubMainChannel, 'data', 'usernames'])
    const isBlocked = username && mainBlockedUsers && mainBlockedUsers.find((x) => x === username)
    const mainChatClassname = `main-chat${state.currentChat === props.pubNubMainChannel ? ' main-chat--active' : ''}`
    const mainMessages = state[pubNubMainChannel] || List()
    const techMessages = state[pubNubTechChannel] || List()

    return (
      <div className="chat">
        <div className="main-and-tech__container">
          <div className="main-and-tech">
            <div className={mainChatClassname}>
              <ChatHeader
                onToggle={this.toggleChannel}
                staticText={staticText}
                isMain
              />
              <ChatHistory
                fetchHistory={this.onFetchHistory}
                messageList={mainMessages}
                username={this.username}
              />
            </div>
            <div className="tech-chat">
              <ChatHeader
                onToggle={this.toggleChannel}
                staticText={staticText}
              />
              <ChatHistory
                fetchHistory={this.onFetchHistory}
                messageList={techMessages}
                username={this.username}
              />
            </div>
          </div>
        </div>
        <div className="chat-input-container">
          {isBlocked && (
            <div className="chat__blocked-user">
              You have been blocked from this chat.
            </div>
          )}
          {!isBlocked && username && (
            <ChatInput
              imageUrl={imageUrl || EMPTY_AVATAR_URL}
              sendMessage={this.sendMessage}
              staticText={staticText}
            />
          )}
          {!isBlocked && !username && (
            <div className="chat__anon-message">
              {staticText.getIn(['data', 'joinChat']) || 'Sign in to join the chat'}
            </div>
          )}
        </div>
      </div>
    )
  }
}

const ChatPopOutPage = (props) => {
  const {
    eventId,
    pubNubPublishKey,
    pubNubSubscribeKey,
    pubNubMainChannel,
    pubNubTechChannel,
    username,
    imageUrl,
    getBlockedUsers,
    getChatList,
    staticText,
    blockedUsers,
    chatInfoFromRedux,
    chatListProcessing,
  } = props

  // Fetch chatInfo if eventId is provided
  useEffect(() => {
    if (eventId && getChatList) {
      getChatList(eventId)
    }
  }, [eventId, getChatList])

  // Prevent body/html scrollbars on mount
  useEffect(() => {
    const originalBodyOverflow = document.body.style.overflow
    const originalHtmlOverflow = document.documentElement.style.overflow
    const originalBodyHeight = document.body.style.height
    const originalHtmlHeight = document.documentElement.style.height

    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
    document.body.style.height = '100%'
    document.documentElement.style.height = '100%'

    return () => {
      document.body.style.overflow = originalBodyOverflow
      document.documentElement.style.overflow = originalHtmlOverflow
      document.body.style.height = originalBodyHeight
      document.documentElement.style.height = originalHtmlHeight
    }
  }, [])

  // Determine chatInfo source: from Redux (if eventId provided) or from URL params
  const chatInfo = eventId && chatInfoFromRedux
    ? chatInfoFromRedux
    : {
      pubNubPublishKey,
      pubNubSubscribeKey,
      pubNubMainChannel,
      pubNubTechChannel,
    }

  // Show loading state if fetching chatInfo
  if (eventId && chatListProcessing) {
    return (
      <div className="chat-popout-page chat-popout-page--error">
        <div className="chat-popout-page__error-message">
          <H3>Loading Chat Configuration...</H3>
          <p>Fetching chat information for event...</p>
        </div>
      </div>
    )
  }

  // Validate required parameters
  if (!chatInfo.pubNubPublishKey || !chatInfo.pubNubSubscribeKey
    || !chatInfo.pubNubMainChannel || !chatInfo.pubNubTechChannel) {
    return (
      <div className="chat-popout-page chat-popout-page--error">
        <div className="chat-popout-page__error-message">
          <H3>Chat Configuration Error</H3>
          <p>Missing required chat parameters.</p>
          {eventId ? (
            <p>
              Unable to fetch chat information for event ID: {eventId}.
              Please check the event ID or use direct URL parameters.
            </p>
          ) : (
            <>
              <p>
                Please provide either an eventId parameter or all required chat
                parameters in the URL.
              </p>
              <p>
                Required parameters: eventId OR (pubNubPublishKey,
                pubNubSubscribeKey, pubNubMainChannel, pubNubTechChannel)
              </p>
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="chat-popout-page">
      <PopOutChat
        pubNubPublishKey={chatInfo.pubNubPublishKey}
        pubNubSubscribeKey={chatInfo.pubNubSubscribeKey}
        pubNubMainChannel={chatInfo.pubNubMainChannel}
        pubNubTechChannel={chatInfo.pubNubTechChannel}
        username={username || ''}
        imageUrl={imageUrl || ''}
        staticText={staticText}
        blockedUsers={blockedUsers}
        getBlockedUsers={getBlockedUsers}
      />
    </div>
  )
}

const extractUrlParams = (location) => {
  if (!location || !location.search) {
    return {}
  }

  const params = new URLSearchParams(location.search)
  return {
    eventId: params.get('eventId') || '',
    pubNubPublishKey: params.get('pubNubPublishKey') || '',
    pubNubSubscribeKey: params.get('pubNubSubscribeKey') || '',
    pubNubMainChannel: params.get('pubNubMainChannel') || '',
    pubNubTechChannel: params.get('pubNubTechChannel') || '',
    username: params.get('username') || '',
    imageUrl: params.get('imageUrl') || '',
  }
}

export default compose(
  connectRedux(
    (state, ownProps) => {
      const urlParams = extractUrlParams(ownProps.location)
      const { eventId } = urlParams

      // If eventId is provided, fetch chatInfo from Redux
      let chatInfoFromRedux = null
      let chatListProcessing = false
      if (eventId) {
        const chatInfos = state.liveAccessEvents.getIn([chatListStoreKey, eventId, 'data', 'chatInfo'])
        chatInfoFromRedux = getChatInfo(chatInfos)
        chatListProcessing = state.liveAccessEvents.getIn([chatListStoreKey, eventId, 'processing'], false)
      }

      // Determine main channel for blocked users lookup
      const mainChannel = chatInfoFromRedux?.pubNubMainChannel || urlParams.pubNubMainChannel
      const blockedUsers = mainChannel
        ? state.liveAccessEvents.getIn([blockedChatUsersKey, mainChannel])
        : Map()

      // Get username from Redux auth state (prefer over URL param)
      const username = state.auth.get('username') || urlParams.username || ''
      // Get imageUrl from Redux user state (prefer over URL param)
      const currentPic = selectCurrentUserProfilePicture(state)
      const imageUrl = currentPic
        ? currentPic.small
        : state.user.getIn(['data', 'profile', 'picture', 'hdtv_190x266'])
          || urlParams.imageUrl || ''

      return {
        ...urlParams,
        username, // Override with Redux value
        imageUrl, // Override with Redux value
        blockedUsers,
        chatInfoFromRedux,
        chatListProcessing,
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        getBlockedUsers: actions.liveAccessEvents.getLiveAccessChatBlockedUsers,
        getChatList: actions.liveAccessEvents.getLiveAccessChatList,
      }
    },
  ),
  connectStaticText({ storeKey: 'liveChat' }),
)(ChatPopOutPage)
